import type { FC, ReactNode } from 'react'
import type { TeamId } from '@/types'

import { useEffect, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import OrbitHub from '@/components/landing/orbit-hub'

import {
    COORDINATOR_COLOR,
    agentLookup,
    coordinator,
    teams
} from '@/constants/agents'

const STEP_DURATION = 4200

const steps = [
    {
        title: 'You ask',
        text: "Tell the Coordinator what you need in plain words, the way you would brief a teammate."
    },
    {
        title: 'The Coordinator plans',
        text: "It breaks your request into tasks and picks the right specialist for each one."
    },
    {
        title: 'Teams work in parallel',
        text: "Marketing, Lead Generation, Email Outreach and Bug Fixing all get moving at the same time."
    },
    {
        title: 'Results come back to you',
        text: "The Coordinator checks the work and hands you one clear summary of what got done."
    }
]

const HowItWorksSection: FC = (): ReactNode => {
    const [step, setStep] = useState(0)
    const [autoplay, setAutoplay] = useState(true)
    const [motionOn, setMotionOn] = useState(true)
    const [focusTeam, setFocusTeam] = useState<TeamId | null>(null)
    const [hoveredId, setHoveredId] = useState<string | null>(null)
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const reducedMotion = useReducedMotion()

    useEffect(() => {
        if (!reducedMotion) return

        setAutoplay(false)
        setMotionOn(false)
    }, [reducedMotion])

    useEffect(() => {
        if (!autoplay) return

        const timer = window.setInterval(() => {
            setStep((current) => (current + 1) % steps.length)
        }, STEP_DURATION)

        return () => window.clearInterval(timer)
    }, [autoplay])

    const activeId = hoveredId ?? selectedId
    const activeEntry = activeId ? agentLookup.get(activeId) : undefined
    const focusedTeam = focusTeam ? teams.find((team) => team.id === focusTeam) : undefined

    const chooseStep = (index: number): void => {
        setStep(index)
        setAutoplay(false)
    }

    const toggleMotion = (): void => {
        const next = !motionOn

        setMotionOn(next)
        setAutoplay(next)
    }

    const toggleSelect = (id: string): void => {
        setSelectedId((current) => (current === id ? null : id))
    }

    const renderDetail = (): ReactNode => {
        if (activeEntry) {
            const { agent, team } = activeEntry
            const Icon = agent.icon

            return (
                <>
                    <div className='flex items-center gap-3'>
                        <span
                            className='flex size-11 shrink-0 items-center justify-center rounded-xl border'
                            style={{ borderColor: `${team.color}66`, backgroundColor: `${team.color}1a`, color: team.color }}
                        >
                            <Icon className='size-5' aria-hidden='true' />
                        </span>

                        <div>
                            <p className='text-xs font-medium uppercase tracking-wider' style={{ color: team.color }}>
                                {team.name} team
                            </p>
                            <h3 className='font-heading text-lg font-semibold'>
                                {agent.name}
                            </h3>
                        </div>
                    </div>

                    <p className='mt-4 text-sm leading-relaxed text-muted-foreground'>
                        {agent.role}
                    </p>

                    <div className='mt-4 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5'>
                        <p className='font-mono text-[11px] uppercase tracking-wider text-muted-foreground'>
                            Example output
                        </p>
                        <p className='mt-1 text-sm'>
                            {agent.example}
                        </p>
                    </div>
                </>
            )
        }

        if (focusedTeam && activeId !== 'coordinator') {
            const Icon = focusedTeam.icon

            return (
                <>
                    <div className='flex items-center gap-3'>
                        <span
                            className='flex size-11 shrink-0 items-center justify-center rounded-xl border'
                            style={{ borderColor: `${focusedTeam.color}66`, backgroundColor: `${focusedTeam.color}1a`, color: focusedTeam.color }}
                        >
                            <Icon className='size-5' aria-hidden='true' />
                        </span>

                        <div>
                            <p className='text-xs font-medium uppercase tracking-wider' style={{ color: focusedTeam.color }}>
                                {focusedTeam.tagline}
                            </p>
                            <h3 className='font-heading text-lg font-semibold'>
                                {focusedTeam.name} team
                            </h3>
                        </div>
                    </div>

                    <p className='mt-4 text-sm leading-relaxed text-muted-foreground'>
                        {focusedTeam.summary}
                    </p>

                    <ul className='mt-4 flex flex-wrap gap-2'>
                        {focusedTeam.agents.map((agent) => (
                            <li key={agent.id}>
                                <button
                                    type='button'
                                    onClick={() => toggleSelect(agent.id)}
                                    className='rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium transition-colors hover:bg-white/10'
                                >
                                    {agent.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            )
        }

        const Icon = coordinator.icon

        return (
            <>
                <div className='flex items-center gap-3'>
                    <span
                        className='flex size-11 shrink-0 items-center justify-center rounded-xl border'
                        style={{ borderColor: `${COORDINATOR_COLOR}66`, backgroundColor: `${COORDINATOR_COLOR}1a`, color: COORDINATOR_COLOR }}
                    >
                        <Icon className='size-5' aria-hidden='true' />
                    </span>

                    <div>
                        <p className='text-xs font-medium uppercase tracking-wider text-primary'>
                            Center of the system
                        </p>
                        <h3 className='font-heading text-lg font-semibold'>
                            {coordinator.name}
                        </h3>
                    </div>
                </div>

                <p className='mt-4 text-sm leading-relaxed text-muted-foreground'>
                    {coordinator.role}
                </p>

                <p className='mt-4 text-xs text-muted-foreground'>
                    Hover or tap any agent in the orbit to see what it does.
                </p>
            </>
        )
    }

    return (
        <section id='how-it-works' className='relative overflow-hidden py-24 sm:py-32'>
            <div aria-hidden='true' className='bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]' />

            <div className='relative mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='mx-auto max-w-2xl text-center'>
                    <p className='font-mono text-xs uppercase tracking-[0.2em] text-primary'>
                        How it works
                    </p>
                    <h2 className='mt-4 font-heading text-3xl font-semibold tracking-tight text-balance sm:text-5xl'>
                        One Coordinator at the center. Twenty specialists in orbit.
                    </h2>
                    <p className='mt-5 text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                        Every request goes through the Coordinator. It sends the work out to four teams of specialists and brings the results back to you.
                    </p>
                </div>

                <div className='mt-14 grid items-center gap-12 lg:mt-20 lg:grid-cols-12 lg:gap-10'>
                    <div className='order-2 lg:order-1 lg:col-span-5'>
                        <div className='flex items-center justify-between'>
                            <p className='text-sm font-medium text-muted-foreground'>
                                The workflow
                            </p>

                            <button
                                type='button'
                                onClick={toggleMotion}
                                className='inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground'
                            >
                                {motionOn
                                    ? <Pause className='size-3.5' aria-hidden='true' />
                                    : <Play className='size-3.5' aria-hidden='true' />}
                                {motionOn ? 'Pause' : 'Play'}
                            </button>
                        </div>

                        <ol className='mt-4 space-y-2'>
                            {steps.map((item, index) => {
                                const active = index === step

                                return (
                                    <li key={item.title}>
                                        <button
                                            type='button'
                                            onClick={() => chooseStep(index)}
                                            aria-current={active ? 'step' : undefined}
                                            className={cn(
                                                'relative flex w-full gap-4 overflow-hidden rounded-xl border px-4 py-3.5 text-left transition-colors',
                                                active ? 'border-primary/40 bg-primary/10' : 'border-transparent hover:bg-white/[0.04]'
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'flex size-7 shrink-0 items-center justify-center rounded-full font-mono text-xs font-semibold transition-colors',
                                                    active ? 'bg-primary text-primary-foreground' : 'bg-white/10 text-muted-foreground'
                                                )}
                                            >
                                                {index + 1}
                                            </span>

                                            <span>
                                                <span className={cn('block font-medium', active ? 'text-foreground' : 'text-foreground/80')}>
                                                    {item.title}
                                                </span>
                                                <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>
                                                    {item.text}
                                                </span>
                                            </span>

                                            {active && autoplay && (
                                                <span
                                                    key={`progress-${step}`}
                                                    aria-hidden='true'
                                                    className='absolute inset-x-0 bottom-0 h-0.5 origin-left bg-primary/70'
                                                    style={{ animation: `step-progress ${STEP_DURATION}ms linear forwards` }}
                                                />
                                            )}
                                        </button>
                                    </li>
                                )
                            })}
                        </ol>

                        <div
                            aria-live='polite'
                            className='mt-6 min-h-[236px] rounded-2xl border border-white/10 bg-card/80 p-5 shadow-2xl shadow-black/20 backdrop-blur'
                        >
                            {renderDetail()}
                        </div>
                    </div>

                    <div className='order-1 lg:order-2 lg:col-span-7'>
                        <div className='mb-6 flex flex-wrap justify-center gap-2' role='group' aria-label='Show a team'>
                            <button
                                type='button'
                                aria-pressed={focusTeam === null}
                                onClick={() => setFocusTeam(null)}
                                className={cn(
                                    'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                                    focusTeam === null ? 'border-white/30 bg-white/10 text-foreground' : 'border-white/10 text-muted-foreground hover:text-foreground'
                                )}
                            >
                                All teams
                            </button>

                            {teams.map((team) => {
                                const active = focusTeam === team.id

                                return (
                                    <button
                                        key={team.id}
                                        type='button'
                                        aria-pressed={active}
                                        onClick={() => setFocusTeam(active ? null : team.id)}
                                        className={cn(
                                            'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                                            active ? 'bg-white/10 text-foreground' : 'border-white/10 text-muted-foreground hover:text-foreground'
                                        )}
                                        style={active ? { borderColor: `${team.color}99` } : undefined}
                                    >
                                        <span className='size-2 rounded-full' style={{ backgroundColor: team.color }} aria-hidden='true' />
                                        {team.name}
                                    </button>
                                )
                            })}
                        </div>

                        <OrbitHub
                            step={step}
                            focusTeam={focusTeam}
                            activeId={activeId}
                            paused={!motionOn}
                            reducedMotion={reducedMotion}
                            onHover={setHoveredId}
                            onSelect={toggleSelect}
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HowItWorksSection
